// src/pages/Admin/Marketing/MarketingPage.jsx
// NAV entry: { path: "/admin/marketing", label: "Marketing", icon: MarketingIcon }
// Route:     <Route path="marketing" element={<MarketingPage />} />

import { useState, useEffect, useRef, useCallback } from "react";

// ─── Template data with sections ────────────────────────────────────────────
const TEMPLATES = [
  // Campaigns
  { id:1,  name:"claimpro_offer",            display:"Roadside Assist Launch",      section:"Campaigns",  status:"APPROVED", sentCount:4200, openRate:68, createdAt:"12 Jun 2026", variables:[],
    body:"🚗 ClaimPro Assist is now providing 24x7 roadside assistance, towing, and claim support services.\n\nNeed assistance? Reply HELP or call us at +91 98765 43210." },
  { id:2,  name:"festival_offer_2026",       display:"Festival Season Offer",        section:"Campaigns",  status:"APPROVED", sentCount:0,    openRate:0,  createdAt:"14 Jun 2026", variables:[],
    body:"🎉 This festive season, ClaimPro brings you priority claim processing & complimentary roadside assistance.\n\nValid till 31st Oct 2026. T&C apply.\n\nReply INFO for details." },
  { id:3,  name:"year_end_campaign",         display:"Year-End Customer Drive",      section:"Campaigns",  status:"PENDING",  sentCount:0,    openRate:0,  createdAt:"16 Jun 2026", variables:[],
    body:"🎊 Thank you for being with ClaimPro this year! As a valued customer, enjoy exclusive year-end benefits on all our services.\n\nReply OFFER to know more." },
  // Launches
  { id:4,  name:"claim_portal_launch",       display:"Claim Status Portal Launch",   section:"Launches",   status:"PENDING",  sentCount:0,    openRate:0,  createdAt:"15 Jun 2026", variables:[],
    body:"📱 Track your claim in real-time!\n\nOur new claims portal is now live. Login at claimpro.in/portal with your registered mobile number.\n\nGet instant updates on your case status." },
  { id:5,  name:"whatsapp_bot_launch",       display:"WhatsApp Bot Launch",          section:"Launches",   status:"APPROVED", sentCount:1200, openRate:71, createdAt:"1 Jun 2026",  variables:[],
    body:"💬 Exciting news! You can now register accident cases and request quick services directly on WhatsApp.\n\nJust send 'HI' to get started. Available 24x7." },
  { id:6,  name:"mobile_app_launch",         display:"ClaimPro Mobile App",          section:"Launches",   status:"APPROVED", sentCount:890,  openRate:65, createdAt:"20 May 2026", variables:[],
    body:"📲 The ClaimPro Assist mobile app is here!\n\nTrack claims, upload documents, and get real-time updates — all from your phone.\n\nDownload now: claimpro.in/app" },
  // Reminders
  { id:7,  name:"insurance_renewal_reminder",display:"Insurance Renewal Reminder",   section:"Reminders",  status:"APPROVED", sentCount:1820, openRate:74, createdAt:"8 Jun 2026",  variables:["customer_name"],
    body:"⏰ Hi {{customer_name}}, your vehicle insurance is due for renewal soon.\n\nLet ClaimPro Assist handle the paperwork — hassle-free renewal in under 10 minutes.\n\nReply YES to proceed." },
  { id:8,  name:"document_pending_reminder", display:"Pending Documents Reminder",   section:"Reminders",  status:"APPROVED", sentCount:640,  openRate:80, createdAt:"3 Jun 2026",  variables:["customer_name","case_no"],
    body:"📄 Hi {{customer_name}}, we're still waiting for documents for your case {{case_no}}.\n\nPlease upload them at claimpro.in/portal or reply DOCS to get the list." },
  { id:9,  name:"survey_appointment_remind", display:"Survey Appointment Reminder",  section:"Reminders",  status:"APPROVED", sentCount:420,  openRate:76, createdAt:"28 May 2026", variables:["customer_name","date","time"],
    body:"🔔 Reminder: Your vehicle survey is scheduled for {{date}} at {{time}}.\n\nPlease ensure the vehicle is accessible. Reply RESCHEDULE if needed." },
  // Offers
  { id:10, name:"free_inspection_offer",     display:"Free Vehicle Inspection",      section:"Offers",     status:"APPROVED", sentCount:3100, openRate:61, createdAt:"5 Jun 2026",  variables:[],
    body:"🔧 Exclusive offer for ClaimPro customers!\n\nGet a FREE vehicle inspection this month. Our certified mechanics will check your vehicle at no cost.\n\nBook your slot — reply BOOK or visit claimpro.in/inspect" },
  { id:11, name:"crane_discount_offer",      display:"Crane Service Discount",       section:"Offers",     status:"PENDING",  sentCount:0,    openRate:0,  createdAt:"17 Jun 2026", variables:[],
    body:"🚛 Special offer! Get 20% off on crane and towing services this month.\n\nValid for all ClaimPro registered customers. Call us or reply CRANE to avail." },
  // Utility
  { id:12, name:"fasttag_challan_alert",     display:"FASTag / Challan Services",    section:"Utility",    status:"REJECTED", sentCount:0,    openRate:0,  createdAt:"10 Jun 2026", variables:[],
    body:"🚦 Unpaid challan or FASTag issue? ClaimPro Assist can help you resolve it quickly.\n\nReply CHALLAN to get started." },
  { id:13, name:"claim_settled_notify",      display:"Claim Settled Notification",   section:"Utility",    status:"APPROVED", sentCount:2100, openRate:90, createdAt:"1 May 2026",  variables:["customer_name","amount"],
    body:"✅ Great news, {{customer_name}}! Your insurance claim has been settled.\n\nAmount: ₹{{amount}} has been processed.\n\nFor queries, reply QUERY or call us." },
];

const SECTIONS = [
  { key:"Campaigns", icon:"📣", color:"#3b82f6", bg:"#1e3a5f",   desc:"Broad outreach campaigns" },
  { key:"Launches",  icon:"🚀", color:"#a855f7", bg:"#2d1a45",   desc:"New feature & service announcements" },
  { key:"Reminders", icon:"⏰", color:"#f97316", bg:"#3d2010",   desc:"Follow-up & deadline nudges" },
  { key:"Offers",    icon:"🎁", color:"#22c55e", bg:"#0f3021",   desc:"Discounts & special deals" },
  { key:"Utility",   icon:"⚙️", color:"#14b8a6", bg:"#0c2d2b",  desc:"Transactional & service updates" },
];

const SEGMENT_CONFIG = [
  { key:"all",         label:"All Customers",         count:4821, color:"#3b82f6", bg:"#1e3a5f", icon:"👥" },
  { key:"active_cases",label:"Have Active Claims",    count:3104, color:"#a855f7", bg:"#2d1a45", icon:"📋" },
  { key:"recent_30d",  label:"Active Last 30 Days",   count:892,  color:"#22c55e", bg:"#0f3021", icon:"🟢" },
  { key:"new_month",   label:"Registered This Month", count:217,  color:"#f97316", bg:"#3d2010", icon:"🆕" },
  { key:"no_survey",   label:"Awaiting Survey",       count:156,  color:"#eab308", bg:"#2d2210", icon:"🔍" },
  { key:"pending_del", label:"Pending Delivery",      count:89,   color:"#14b8a6", bg:"#0c2d2b", icon:"🚚" },
];

const CATEGORY_OPTIONS = ["Marketing","Utility","Authentication"];
const VIEWS = { LIST:"list", CREATE:"create", COMPOSE:"compose", DONE:"done" };

// ─── Global styles (injected once) ──────────────────────────────────────────
const GLOBAL_CSS = `
  @keyframes mkToast  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
  @keyframes mkSpin   { to{transform:rotate(360deg)} }
  @keyframes mkPulse  { 0%,100%{opacity:1} 50%{opacity:.35} }
  @keyframes mkSlide  { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:none} }
  .mk-tmpl-card       { transition:border-color .15s,background .15s; }
  .mk-tmpl-card:hover { border-color:rgba(33,166,233,.45) !important; background:rgba(33,166,233,.04) !important; }
  .mk-btn-ghost       { background:transparent; border:1px solid rgba(255,255,255,.1); color:rgba(255,255,255,.45); border-radius:8px; padding:8px 16px; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
  .mk-btn-ghost:hover { border-color:rgba(255,255,255,.25); color:rgba(255,255,255,.7); }
  .mk-btn-ghost-sm    { background:transparent; border:1px solid rgba(255,255,255,.1); color:rgba(255,255,255,.45); border-radius:8px; padding:6px 12px; font-size:12px; font-weight:600; cursor:pointer; transition:all .15s; }
  .mk-btn-ghost-sm:hover { border-color:rgba(255,255,255,.25); color:rgba(255,255,255,.7); }
  .mk-tab             { padding:7px 16px; border-radius:8px; font-size:12px; font-weight:700; cursor:pointer; transition:all .15s; border:none; }
  .mk-tab-active      { background:rgba(33,166,233,.15); color:#21A6E9; }
  .mk-tab-idle        { background:transparent; color:rgba(255,255,255,.35); }
  .mk-tab-idle:hover  { color:rgba(255,255,255,.6); background:rgba(255,255,255,.04); }
  .mk-input           { background:rgba(255,255,255,.04) !important; border:1px solid rgba(255,255,255,.1) !important; border-radius:8px; color:#e8eaf0 !important; padding:9px 12px; font-size:13px; outline:none; transition:border-color .15s; width:100%; box-sizing:border-box; }
  .mk-input:focus     { border-color:rgba(33,166,233,.55) !important; }
  .mk-textarea        { background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.1); border-radius:8px; color:#e8eaf0; padding:10px 12px; font-size:13px; outline:none; transition:border-color .15s; width:100%; box-sizing:border-box; resize:vertical; min-height:140px; line-height:1.65; font-family:inherit; }
  .mk-textarea:focus  { border-color:rgba(33,166,233,.55); }
  .mk-btn-primary     { background:#21A6E9; border:none; color:#fff; border-radius:9px; padding:10px 22px; font-size:13px; font-weight:700; cursor:pointer; transition:opacity .15s,transform .1s; }
  .mk-btn-primary:hover:not(:disabled)  { opacity:.88; }
  .mk-btn-primary:active:not(:disabled) { transform:scale(.98); }
  .mk-btn-primary:disabled { background:rgba(255,255,255,.08); color:rgba(255,255,255,.2); cursor:not-allowed; }
  .mk-btn-send        { width:100%; padding:14px; border-radius:10px; background:#21A6E9; border:none; color:#fff; font-size:14px; font-weight:800; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px; transition:opacity .15s,transform .1s; letter-spacing:.01em; }
  .mk-btn-send:hover:not(:disabled)  { opacity:.88; }
  .mk-btn-send:active:not(:disabled) { transform:scale(.98); }
  .mk-btn-send:disabled { background:rgba(33,166,233,.3); cursor:not-allowed; }
  .mk-progress-fill   { height:100%; border-radius:3px; background:#21A6E9; transition:width .12s linear; }
  .mk-dropdown-opt    { padding:9px 14px; font-size:13px; color:#e8eaf0; cursor:pointer; transition:background .1s; border-radius:6px; }
  .mk-dropdown-opt:hover { background:rgba(33,166,233,.12); color:#21A6E9; }
  .mk-dropdown-opt.active { background:rgba(33,166,233,.15); color:#21A6E9; }
  .mk-field-label     { font-size:11px; font-weight:700; color:rgba(255,255,255,.4); text-transform:uppercase; letter-spacing:.08em; margin-bottom:7px; display:block; }
  .mk-section-chip    { padding:6px 14px; border-radius:20px; font-size:12px; font-weight:700; cursor:pointer; border:1.5px solid; transition:all .15s; white-space:nowrap; }
`;

// ─── Shared card style ───────────────────────────────────────────────────────
const CARD = { background:"var(--wd-card)", border:"1px solid var(--wd-border)", borderRadius:12, padding:"18px 20px" };

// ─── StatusBadge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const M = {
    APPROVED:{ bg:"rgba(34,197,94,.15)",  text:"#22c55e", border:"rgba(34,197,94,.3)",  dot:"#22c55e" },
    PENDING: { bg:"rgba(234,179,8,.15)",  text:"#eab308", border:"rgba(234,179,8,.3)",  dot:"#eab308" },
    REJECTED:{ bg:"rgba(239,68,68,.15)",  text:"#ef4444", border:"rgba(239,68,68,.3)",  dot:"#ef4444" },
  };
  const c = M[status]||M.PENDING;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", borderRadius:20,
      background:c.bg, color:c.text, border:`1px solid ${c.border}`, fontSize:10, fontWeight:800, letterSpacing:".05em" }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:c.dot, flexShrink:0 }} />
      {status}
    </span>
  );
}

// ─── WhatsApp bubble ─────────────────────────────────────────────────────────
function WaBubble({ body, compact=false }) {
  return (
    <div style={{ background:"#0b1b2b", borderRadius:10, padding:compact?"10px 12px":"14px 16px", border:"1px solid rgba(37,211,102,.15)" }}>
      {!compact && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10, paddingBottom:8, borderBottom:"1px solid rgba(255,255,255,.06)" }}>
          <div style={{ width:28, height:28, borderRadius:"50%", background:"#21A6E9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#fff", flexShrink:0 }}>C</div>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:"#e8eaf0" }}>ClaimPro Assist</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,.3)" }}>Business Account · WhatsApp</div>
          </div>
        </div>
      )}
      <div style={{ background:"rgba(33,166,233,.1)", border:"1px solid rgba(33,166,233,.2)", borderRadius:"2px 10px 10px 10px", padding:"9px 12px", maxWidth:"88%" }}>
        <div style={{ fontSize:compact?11:13, color:"#e8eaf0", lineHeight:1.65, whiteSpace:"pre-wrap" }}>{body||"No preview"}</div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,.25)", textAlign:"right", marginTop:4 }}>
          {new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})} ✓✓
        </div>
      </div>
    </div>
  );
}

// ─── Animated number ─────────────────────────────────────────────────────────
function AnimCount({ to, duration=700 }) {
  const [v,setV] = useState(0);
  const raf = useRef();
  useEffect(()=>{
    const from=v, start=performance.now();
    const tick=(now)=>{
      const p=Math.min((now-start)/duration,1), e=1-Math.pow(1-p,3);
      setV(Math.round(from+(to-from)*e));
      if(p<1) raf.current=requestAnimationFrame(tick);
    };
    raf.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(raf.current);
  },[to]);
  return <>{v.toLocaleString("en-IN")}</>;
}

// ─── Step bar ────────────────────────────────────────────────────────────────
function StepBar({ steps, current }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:0, marginBottom:24 }}>
      {steps.map((s,i)=>{
        const st = i<current?"done":i===current?"active":"idle";
        return (
          <div key={i} style={{ display:"flex", alignItems:"center", flex:i<steps.length-1?"1":undefined }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
              <div style={{ width:30, height:30, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                background:st==="done"?"#21A6E9":st==="active"?"rgba(33,166,233,.15)":"rgba(255,255,255,.04)",
                border:st==="done"?"none":st==="active"?"2px solid #21A6E9":"1px solid rgba(255,255,255,.12)", transition:"all .25s" }}>
                {st==="done"
                  ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2.5 6.5l2.5 2.5L10.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <span style={{ fontSize:12, fontWeight:700, color:st==="active"?"#21A6E9":"rgba(255,255,255,.2)" }}>{i+1}</span>
                }
              </div>
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:".05em", textTransform:"uppercase",
                color:st==="active"?"#e8eaf0":st==="done"?"#21A6E9":"rgba(255,255,255,.2)", whiteSpace:"nowrap" }}>{s}</span>
            </div>
            {i<steps.length-1 && (
              <div style={{ flex:1, height:2, background:i<current?"#21A6E9":"rgba(255,255,255,.07)", margin:"-14px 8px 0", transition:"background .3s" }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Custom dropdown (no native select) ─────────────────────────────────────
function CustomSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(()=>{
    const h=(e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",h);
    return ()=>document.removeEventListener("mousedown",h);
  },[]);
  return (
    <div ref={ref} style={{ position:"relative", userSelect:"none" }}>
      <div onClick={()=>setOpen(o=>!o)} style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"rgba(255,255,255,.04)", border:`1px solid ${open?"rgba(33,166,233,.55)":"rgba(255,255,255,.1)"}`,
        borderRadius:8, padding:"9px 12px", fontSize:13, color:"#e8eaf0", cursor:"pointer",
        transition:"border-color .15s",
      }}>
        <span>{value}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transition:"transform .2s", transform:open?"rotate(180deg)":"none", flexShrink:0 }}>
          <path d="M2 4l4 4 4-4" stroke="rgba(255,255,255,.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 5px)", left:0, right:0, zIndex:500,
          background:"#1a1f2e", border:"1px solid rgba(255,255,255,.1)", borderRadius:10,
          padding:"5px", boxShadow:"0 8px 32px rgba(0,0,0,.6)", animation:"mkSlide .15s ease" }}>
          {options.map(opt=>(
            <div key={opt} className={`mk-dropdown-opt${value===opt?" active":""}`} onClick={()=>{ onChange(opt); setOpen(false); }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────
function Toast({ msg, type, onClose }) {
  useEffect(()=>{ const t=setTimeout(onClose,3500); return ()=>clearTimeout(t); },[]);
  const c = type==="success"?{bg:"rgba(34,197,94,.15)",border:"#22c55e",text:"#22c55e"}
           :type==="error"  ?{bg:"rgba(239,68,68,.15)", border:"#ef4444",text:"#ef4444"}
                            :{bg:"rgba(33,166,233,.15)",border:"#21A6E9",text:"#21A6E9"};
  return (
    <div style={{ position:"fixed", bottom:28, right:28, zIndex:9999, padding:"12px 18px", borderRadius:9,
      background:c.bg, border:`1px solid ${c.border}`, color:c.text, fontSize:13, fontWeight:600,
      boxShadow:"0 8px 32px rgba(0,0,0,.5)", maxWidth:340, animation:"mkToast .2s ease" }}>
      {msg}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════════
export default function MarketingPage() {
  const [view, setView]               = useState(VIEWS.LIST);
  const [selectedTmpl, setSelectedTmpl] = useState(null);
  const [previewTmpl, setPreviewTmpl] = useState(null);
  const [composeStep, setComposeStep] = useState(0);
  const [segments, setSegments]       = useState({ all:true });
  const [campaignName, setCampaignName] = useState("");
  const [sending, setSending]         = useState(false);
  const [progress, setProgress]       = useState(0);
  const [logs, setLogs]               = useState([]);
  const [toast, setToast]             = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sectionFilter, setSectionFilter] = useState("ALL");
  const [newTmpl, setNewTmpl]         = useState({ name:"", display:"", body:"", category:"Marketing", section:"Campaigns" });
  const logsRef = useRef();

  const audienceCount = (()=>{
    if(segments.all) return SEGMENT_CONFIG[0].count;
    let c=0;
    SEGMENT_CONFIG.slice(1).forEach(s=>{ if(segments[s.key]) c=Math.max(c,s.count); });
    return Math.min(c, SEGMENT_CONFIG[0].count);
  })();

  const toggleSeg = (key) => {
    if(key==="all"){ setSegments({all:true}); return; }
    setSegments(prev=>{
      const next={...prev, all:false, [key]:!prev[key]};
      if(!Object.keys(next).filter(k=>k!=="all").some(k=>next[k])) return {all:true};
      return next;
    });
  };

  const segLabel = segments.all ? "All customers"
    : SEGMENT_CONFIG.slice(1).filter(s=>segments[s.key]).map(s=>s.label).join(", ");

  const handleSend = useCallback(()=>{
    setSending(true); setProgress(0); setLogs([]);
    const addLog=(msg,ok=false)=>setLogs(l=>[...l,{
      ts:new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit",second:"2-digit"}), msg, ok
    }]);
    addLog(`Campaign "${campaignName||"Untitled"}" initiated`);
    addLog(`Template: ${selectedTmpl.name} · Recipients: ${audienceCount.toLocaleString("en-IN")}`);
    let pct=0;
    const iv=setInterval(()=>{
      pct=Math.min(pct+5,100); setProgress(pct);
      if(pct===50) addLog(`Dispatched ${Math.floor(audienceCount*.5).toLocaleString("en-IN")} / ${audienceCount.toLocaleString("en-IN")} messages`);
      if(pct===100){
        clearInterval(iv);
        addLog(`✓ Campaign complete — ${audienceCount.toLocaleString("en-IN")} messages sent`,true);
        setSending(false); setView(VIEWS.DONE);
        setToast({msg:`Campaign sent to ${audienceCount.toLocaleString("en-IN")} customers`,type:"success"});
      }
    },80);
  },[selectedTmpl, audienceCount, campaignName]);

  useEffect(()=>{ if(logsRef.current) logsRef.current.scrollTop=logsRef.current.scrollHeight; },[logs]);

  const filteredTemplates = TEMPLATES.filter(t=>{
    const statusOk = statusFilter==="ALL" || t.status===statusFilter;
    const sectionOk = sectionFilter==="ALL" || t.section===sectionFilter;
    return statusOk && sectionOk;
  });

  // Group templates by section for the grouped view
  const groupedTemplates = SECTIONS.map(s=>({
    ...s,
    templates: TEMPLATES.filter(t => t.section===s.key && (statusFilter==="ALL"||t.status===statusFilter))
  })).filter(g=>g.templates.length>0);

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: TEMPLATE LIST
  // ════════════════════════════════════════════════════════════════════════
  if(view===VIEWS.LIST) return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <style>{GLOBAL_CSS}</style>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            <div style={{ width:6, height:28, background:"#21A6E9", borderRadius:3 }}/>
            <h1 style={{ margin:0, fontSize:20, fontWeight:800, color:"var(--wd-t1)", letterSpacing:"-.3px" }}>WhatsApp Marketing</h1>
          </div>
          <p style={{ margin:0, color:"var(--wd-t3)", fontSize:13, paddingLeft:16 }}>
            Broadcast approved templates to your customer base
          </p>
        </div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px", borderRadius:8, background:"rgba(37,211,102,.1)", border:"1px solid rgba(37,211,102,.2)" }}>
            <span style={{ width:7, height:7, borderRadius:"50%", background:"#25d366", animation:"mkPulse 2s infinite", flexShrink:0 }}/>
            <span style={{ fontSize:11, fontWeight:700, color:"#25d366" }}>API Live</span>
          </div>
          <button className="mk-btn-primary" style={{ background:"rgba(33,166,233,.15)", color:"#21A6E9", border:"1px solid rgba(33,166,233,.25)" }}
            onClick={()=>{ setView(VIEWS.CREATE); setNewTmpl({name:"",display:"",body:"",category:"Marketing",section:"Campaigns"}); }}>
            + Create Template
          </button>
          <button className="mk-btn-primary"
            onClick={()=>{ setView(VIEWS.COMPOSE); setComposeStep(0); setSelectedTmpl(null); }}>
            📤 New Campaign
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,minmax(0,1fr))", gap:12 }}>
        {[
          { label:"Templates",     value:TEMPLATES.length,                              sub:`${TEMPLATES.filter(t=>t.status==="APPROVED").length} approved`, color:"#21A6E9", bg:"#0d2a3d" },
          { label:"Total Sent",    value:"14.4K",                                       sub:"all time",             color:"#22c55e", bg:"#0f3021" },
          { label:"Customers",     value:"4,821",                                       sub:"in database",          color:"#a855f7", bg:"#2d1a45" },
          { label:"Avg Open Rate", value:"74%",                                         sub:"last 30 days",         color:"#f97316", bg:"#3d2010" },
        ].map(k=>(
          <div key={k.label} style={{ background:k.bg, borderRadius:12, padding:"16px 18px", minHeight:100 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"rgba(255,255,255,.55)", textTransform:"uppercase", letterSpacing:".07em", marginBottom:8 }}>{k.label}</div>
            <div style={{ fontSize:30, fontWeight:800, color:"#fff", lineHeight:1 }}>{k.value}</div>
            <div style={{ fontSize:11, color:k.color, marginTop:6, fontWeight:600 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Section overview chips */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.3)", textTransform:"uppercase", letterSpacing:".07em", marginRight:4 }}>Section:</span>
        {[{key:"ALL",icon:"🗂️",color:"#94a3b8"},...SECTIONS].map(s=>{
          const isOn = sectionFilter===s.key;
          return (
            <button key={s.key} className="mk-section-chip"
              style={{ borderColor:isOn?s.color:"rgba(255,255,255,.1)", color:isOn?s.color:"rgba(255,255,255,.4)", background:isOn?`${s.bg||"rgba(255,255,255,.03)"}`:"transparent" }}
              onClick={()=>setSectionFilter(s.key)}>
              {s.icon} {s.key}
            </button>
          );
        })}
        <div style={{ width:1, height:20, background:"rgba(255,255,255,.1)", margin:"0 4px" }}/>
        {["ALL","APPROVED","PENDING","REJECTED"].map(t=>(
          <button key={t} className={`mk-tab ${statusFilter===t?"mk-tab-active":"mk-tab-idle"}`} onClick={()=>setStatusFilter(t)}>{t}</button>
        ))}
      </div>

      {/* Template sections — grouped view */}
      {sectionFilter==="ALL" ? (
        <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
          {groupedTemplates.map(section=>(
            <div key={section.key}>
              {/* Section header */}
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                <div style={{ width:34, height:34, borderRadius:9, background:section.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                  {section.icon}
                </div>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:"var(--wd-t1)" }}>{section.key}</div>
                  <div style={{ fontSize:11, color:"var(--wd-t3)" }}>{section.desc}</div>
                </div>
                <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,.25)" }}>{section.templates.length} template{section.templates.length!==1?"s":""}</span>
                  <div style={{ height:1, width:24, background:section.color, opacity:.5 }}/>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:12 }}>
                {section.templates.map(t=><TemplateCard key={t.id} t={t} onPreview={setPreviewTmpl} onUse={()=>{ setSelectedTmpl(t); setComposeStep(1); setView(VIEWS.COMPOSE); }}/>)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Flat grid for filtered section */
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:12 }}>
            {sectionFilter} · <span style={{ color:"var(--wd-t3)", fontWeight:400 }}>{filteredTemplates.length} template{filteredTemplates.length!==1?"s":""}</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:12 }}>
            {filteredTemplates.map(t=><TemplateCard key={t.id} t={t} onPreview={setPreviewTmpl} onUse={()=>{ setSelectedTmpl(t); setComposeStep(1); setView(VIEWS.COMPOSE); }}/>)}
          </div>
          {filteredTemplates.length===0 && (
            <div style={{ ...CARD, textAlign:"center", padding:"36px 20px", color:"var(--wd-t3)", fontSize:13 }}>
              No templates found for this filter
            </div>
          )}
        </div>
      )}

      {/* Preview modal */}
      {previewTmpl && (
        <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,.75)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={()=>setPreviewTmpl(null)}>
          <div style={{ background:"#161a24", border:"1px solid rgba(255,255,255,.1)", borderRadius:14, padding:"26px 28px", maxWidth:460, width:"100%" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:18 }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  {SECTIONS.find(s=>s.key===previewTmpl.section) && (
                    <span style={{ fontSize:16 }}>{SECTIONS.find(s=>s.key===previewTmpl.section).icon}</span>
                  )}
                  <span style={{ fontSize:16, fontWeight:800, color:"#e8eaf0" }}>{previewTmpl.display}</span>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  <StatusBadge status={previewTmpl.status}/>
                  <span style={{ fontSize:10, fontFamily:"var(--wd-mono)", color:"rgba(255,255,255,.3)" }}>{previewTmpl.name}</span>
                  <span style={{ fontSize:10, color:"rgba(255,255,255,.25)" }}>· {previewTmpl.section}</span>
                  <span style={{ fontSize:10, color:"rgba(255,255,255,.2)" }}>· {previewTmpl.createdAt}</span>
                </div>
              </div>
              <button onClick={()=>setPreviewTmpl(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,.3)", cursor:"pointer", fontSize:20, padding:0, lineHeight:1 }}>✕</button>
            </div>
            <WaBubble body={previewTmpl.body}/>
            {previewTmpl.variables.length>0 && (
              <div style={{ marginTop:12, padding:"10px 12px", background:"rgba(234,179,8,.07)", border:"1px solid rgba(234,179,8,.2)", borderRadius:8 }}>
                <span style={{ fontSize:11, color:"#eab308", fontWeight:700 }}>Variables: </span>
                {previewTmpl.variables.map(v=>(
                  <code key={v} style={{ fontSize:10, background:"rgba(255,255,255,.08)", padding:"1px 6px", borderRadius:4, fontFamily:"var(--wd-mono)", color:"#e8eaf0", marginLeft:5 }}>{`{{${v}}}`}</code>
                ))}
              </div>
            )}
            {previewTmpl.sentCount>0 && (
              <div style={{ marginTop:10, display:"flex", gap:16 }}>
                <span style={{ fontSize:12, color:"var(--wd-t3)" }}>Sent: <b style={{ color:"#21A6E9" }}>{previewTmpl.sentCount.toLocaleString("en-IN")}</b></span>
                <span style={{ fontSize:12, color:"var(--wd-t3)" }}>Open rate: <b style={{ color:"#22c55e" }}>{previewTmpl.openRate}%</b></span>
              </div>
            )}
            <div style={{ display:"flex", gap:10, marginTop:18 }}>
              <button className="mk-btn-ghost-sm" style={{ flex:1 }} onClick={()=>setPreviewTmpl(null)}>Close</button>
              {previewTmpl.status==="APPROVED" && (
                <button className="mk-btn-primary" style={{ flex:2 }}
                  onClick={()=>{ setSelectedTmpl(previewTmpl); setComposeStep(1); setView(VIEWS.COMPOSE); setPreviewTmpl(null); }}>
                  Use in Campaign →
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: CREATE TEMPLATE
  // ════════════════════════════════════════════════════════════════════════
  if(view===VIEWS.CREATE) return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <style>{GLOBAL_CSS}</style>

      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <button className="mk-btn-ghost" onClick={()=>setView(VIEWS.LIST)}>← Back</button>
        <div>
          <h1 style={{ margin:0, fontSize:18, fontWeight:800, color:"var(--wd-t1)" }}>Create WhatsApp Template</h1>
          <p style={{ margin:0, fontSize:12, color:"var(--wd-t3)" }}>Templates must be approved by Meta before use</p>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 380px", gap:16, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={CARD}>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:16 }}>Template Details</div>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <label className="mk-field-label">Display Name</label>
                <input className="mk-input" placeholder="e.g. Roadside Assistance Launch"
                  value={newTmpl.display} onChange={e=>setNewTmpl(p=>({...p,display:e.target.value}))}/>
              </div>
              <div>
                <label className="mk-field-label">Template ID <span style={{ color:"rgba(255,255,255,.2)", fontWeight:400, textTransform:"none" }}>(lowercase, underscores only)</span></label>
                <input className="mk-input" placeholder="e.g. claimpro_roadside_launch"
                  value={newTmpl.name} onChange={e=>setNewTmpl(p=>({...p,name:e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,"")}))}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label className="mk-field-label">Category</label>
                  <CustomSelect value={newTmpl.category} onChange={v=>setNewTmpl(p=>({...p,category:v}))} options={CATEGORY_OPTIONS}/>
                </div>
                <div>
                  <label className="mk-field-label">Section</label>
                  <CustomSelect value={newTmpl.section} onChange={v=>setNewTmpl(p=>({...p,section:v}))} options={SECTIONS.map(s=>s.key)}/>
                </div>
              </div>
            </div>
          </div>

          <div style={CARD}>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:6 }}>Message Body</div>
            <div style={{ fontSize:11, color:"var(--wd-t3)", marginBottom:12 }}>
              Use <code style={{ background:"rgba(255,255,255,.08)", padding:"1px 5px", borderRadius:4, fontFamily:"var(--wd-mono)" }}>{"{{variable_name}}"}</code> for dynamic fields like customer name, case number etc.
            </div>
            <textarea className="mk-textarea"
              placeholder={"🚗 ClaimPro Assist is now providing 24x7 roadside assistance...\n\nNeed help? Reply HELP or call +91 XXXXX XXXXX."}
              value={newTmpl.body} onChange={e=>setNewTmpl(p=>({...p,body:e.target.value}))}/>
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
              <span style={{ fontSize:11, color:"rgba(255,255,255,.2)" }}>{newTmpl.body.length} / 1024 characters</span>
              <span style={{ fontSize:11, color:"#eab308" }}>
                {(newTmpl.body.match(/\{\{[^}]+\}\}/g)||[]).length} variable(s)
              </span>
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between" }}>
            <button className="mk-btn-ghost" onClick={()=>setView(VIEWS.LIST)}>Cancel</button>
            <button className="mk-btn-primary"
              disabled={!newTmpl.name||!newTmpl.display||!newTmpl.body}
              onClick={()=>{ setToast({msg:"Template submitted to Meta for approval",type:"info"}); setView(VIEWS.LIST); }}>
              Submit for Meta Approval →
            </button>
          </div>
        </div>

        {/* Right: live preview + guidelines */}
        <div style={{ position:"sticky", top:16, display:"flex", flexDirection:"column", gap:14 }}>
          <div style={CARD}>
            <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.3)", textTransform:"uppercase", letterSpacing:".1em", marginBottom:12 }}>Live Preview</div>
            {newTmpl.body
              ? <WaBubble body={newTmpl.body}/>
              : <div style={{ background:"#0b1b2b", borderRadius:10, padding:"28px 16px", textAlign:"center", border:"1px solid rgba(255,255,255,.06)", color:"rgba(255,255,255,.2)", fontSize:12 }}>Start typing to see preview</div>
            }
          </div>
          <div style={{ ...CARD, background:"rgba(33,166,233,.05)", border:"1px solid rgba(33,166,233,.15)" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#21A6E9", marginBottom:10 }}>Section Guide</div>
            {SECTIONS.map(s=>(
              <div key={s.key} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7, padding:"6px 8px", borderRadius:7, background:newTmpl.section===s.key?"rgba(33,166,233,.1)":"transparent" }}>
                <span style={{ fontSize:14 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:newTmpl.section===s.key?"#21A6E9":"var(--wd-t2)" }}>{s.key}</div>
                  <div style={{ fontSize:10, color:"var(--wd-t3)" }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ ...CARD, background:"rgba(234,179,8,.07)", border:"1px solid rgba(234,179,8,.2)" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#eab308", marginBottom:8 }}>⚠ Meta Guidelines</div>
            {["No promotional language without category = Marketing","No misleading calls to action","Variables must have example values","Include opt-out option for marketing","Review takes 24–72 hours"].map((t,i)=>(
              <div key={i} style={{ fontSize:11, color:"rgba(255,255,255,.45)", marginBottom:5, paddingLeft:8, borderLeft:"2px solid rgba(234,179,8,.3)" }}>{t}</div>
            ))}
          </div>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: COMPOSE CAMPAIGN
  // ════════════════════════════════════════════════════════════════════════
  if(view===VIEWS.COMPOSE) return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <style>{GLOBAL_CSS}</style>

      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:4 }}>
        <button className="mk-btn-ghost" onClick={()=>setView(VIEWS.LIST)}>← Templates</button>
        <h1 style={{ margin:0, fontSize:18, fontWeight:800, color:"var(--wd-t1)" }}>New Campaign</h1>
      </div>

      <StepBar steps={["Choose Template","Select Audience","Review & Send"]} current={composeStep}/>

      {/* STEP 0 */}
      {composeStep===0 && (
        <div>
          {SECTIONS.map(section=>{
            const sectionTmpls = TEMPLATES.filter(t=>t.section===section.key && t.status==="APPROVED");
            if(!sectionTmpls.length) return null;
            return (
              <div key={section.key} style={{ marginBottom:24 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                  <div style={{ width:30, height:30, borderRadius:8, background:section.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>{section.icon}</div>
                  <div style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>{section.key}</div>
                  <div style={{ fontSize:11, color:"var(--wd-t3)" }}>· {section.desc}</div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:10 }}>
                  {sectionTmpls.map(t=>(
                    <div key={t.id} className="mk-tmpl-card"
                      style={{ border:`1.5px solid ${selectedTmpl?.id===t.id?"#21A6E9":"rgba(255,255,255,.07)"}`,
                        background:selectedTmpl?.id===t.id?"rgba(33,166,233,.07)":"rgba(255,255,255,.025)",
                        borderRadius:12, padding:"14px 16px", position:"relative", cursor:"pointer" }}
                      onClick={()=>setSelectedTmpl(t)}>
                      {selectedTmpl?.id===t.id && (
                        <span style={{ position:"absolute", top:10, right:10, width:20, height:20, background:"#21A6E9", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5l2.5 2.5L9 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                      )}
                      <div style={{ fontFamily:"var(--wd-mono)", fontSize:10, color:"rgba(255,255,255,.28)", marginBottom:5 }}>{t.name}</div>
                      <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:8 }}>{t.display}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,.4)", lineHeight:1.55, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{t.body}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
            <button className="mk-btn-ghost" onClick={()=>setView(VIEWS.LIST)}>Cancel</button>
            <button className="mk-btn-primary" disabled={!selectedTmpl} onClick={()=>setComposeStep(1)}>Continue → Audience</button>
          </div>
        </div>
      )}

      {/* STEP 1 */}
      {composeStep===1 && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:16, alignItems:"start" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={CARD}>
                <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:14 }}>Audience Segment</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:10 }}>
                  {SEGMENT_CONFIG.map(s=>{
                    const isOn = s.key==="all"?segments.all:segments[s.key];
                    return (
                      <div key={s.key}
                        style={{ padding:"14px 16px", borderRadius:10, cursor:"pointer", transition:"all .15s",
                          border:`1.5px solid ${isOn?s.color:"rgba(255,255,255,.07)"}`,
                          background:isOn?s.bg:"rgba(255,255,255,.02)" }}
                        onClick={()=>toggleSeg(s.key)}>
                        <div style={{ fontSize:18, marginBottom:6 }}>{s.icon}</div>
                        <div style={{ fontSize:11, fontWeight:700, color:isOn?"#e8eaf0":"rgba(255,255,255,.45)", marginBottom:4, lineHeight:1.3 }}>{s.label}</div>
                        <div style={{ fontSize:17, fontWeight:800, color:isOn?s.color:"rgba(255,255,255,.35)" }}>{s.count.toLocaleString("en-IN")}</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid rgba(255,255,255,.06)" }}>
                  <label className="mk-field-label">Campaign Name <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0, color:"rgba(255,255,255,.2)" }}>(optional)</span></label>
                  <input className="mk-input" placeholder="e.g. Roadside Assist Launch — June 2026" value={campaignName} onChange={e=>setCampaignName(e.target.value)}/>
                </div>
              </div>
            </div>

            {/* Ticker */}
            <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.07)", borderRadius:12, padding:"26px 20px", textAlign:"center", position:"sticky", top:16 }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,.3)", textTransform:"uppercase", letterSpacing:".1em", marginBottom:10 }}>Sending to</div>
              <div style={{ fontSize:50, fontWeight:900, color:"#21A6E9", letterSpacing:"-2px", lineHeight:1, marginBottom:6, fontVariantNumeric:"tabular-nums" }}>
                <AnimCount to={audienceCount}/>
              </div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,.3)", marginBottom:20 }}>customers</div>
              <div style={{ background:"rgba(255,255,255,.03)", border:"1px solid rgba(255,255,255,.06)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
                <div style={{ fontSize:10, color:"rgba(255,255,255,.25)", marginBottom:4, textTransform:"uppercase", letterSpacing:".06em" }}>Est. Meta cost (₹0.58/msg)</div>
                <div style={{ fontSize:20, fontWeight:800, color:"#e8eaf0" }}>₹{Math.round(audienceCount*0.58).toLocaleString("en-IN")}</div>
              </div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,.2)", lineHeight:1.6 }}>Check WhatsApp Manager billing limits before large sends</div>
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
            <button className="mk-btn-ghost" onClick={()=>setComposeStep(0)}>← Back</button>
            <button className="mk-btn-primary" disabled={audienceCount===0} onClick={()=>setComposeStep(2)}>Continue → Review</button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {composeStep===2 && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div style={CARD}>
                <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:14 }}>Campaign Summary</div>
                {[
                  ["Campaign Name", campaignName||"—"],
                  ["Template",      selectedTmpl?.display],
                  ["Template ID",   <span style={{ fontFamily:"var(--wd-mono)", fontSize:11 }}>{selectedTmpl?.name}</span>],
                  ["Section",       selectedTmpl?.section],
                  ["Language",      "English (en)"],
                  ["Audience",      `${audienceCount.toLocaleString("en-IN")} customers`],
                  ["Segment",       segLabel],
                ].map(([k,v])=>(
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                    <span style={{ fontSize:12, color:"rgba(255,255,255,.35)", flexShrink:0 }}>{k}</span>
                    <span style={{ fontSize:12, color:"var(--wd-t1)", fontWeight:600, textAlign:"right" }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={CARD}>
                <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.3)", textTransform:"uppercase", letterSpacing:".08em", marginBottom:12 }}>Message Preview</div>
                <WaBubble body={selectedTmpl?.body}/>
              </div>
            </div>

            <div style={CARD}>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:16 }}>Pre-send Checklist</div>
              {["Template is APPROVED by Meta","All recipients are registered ClaimPro customers","Phone numbers from customer_master DB","Recipients opted in during registration","Message is relevant and expected"].map((item,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:12 }}>
                  <div style={{ width:18, height:18, borderRadius:4, background:"rgba(34,197,94,.13)", border:"1px solid rgba(34,197,94,.28)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5l2.5 2.5L8 3" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <span style={{ fontSize:12, color:"rgba(255,255,255,.5)", lineHeight:1.55 }}>{item}</span>
                </div>
              ))}
              <div style={{ height:1, background:"rgba(255,255,255,.06)", margin:"14px 0" }}/>
              {sending && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:11 }}>
                    <span style={{ color:"rgba(255,255,255,.35)" }}>Sending…</span>
                    <span style={{ fontWeight:700, color:"#21A6E9" }}>{progress}%</span>
                  </div>
                  <div style={{ height:6, borderRadius:3, background:"rgba(255,255,255,.07)", overflow:"hidden" }}>
                    <div className="mk-progress-fill" style={{ width:`${progress}%` }}/>
                  </div>
                  <div ref={logsRef} style={{ marginTop:10, background:"#090c13", border:"1px solid rgba(255,255,255,.06)", borderRadius:8, padding:"8px 12px", maxHeight:90, overflowY:"auto", fontFamily:"var(--wd-mono)" }}>
                    {logs.map((l,i)=>(
                      <div key={i} style={{ display:"flex", gap:10, marginBottom:2, fontSize:10 }}>
                        <span style={{ color:"rgba(255,255,255,.2)", flexShrink:0 }}>{l.ts}</span>
                        <span style={{ color:l.ok?"#22c55e":"rgba(255,255,255,.45)" }}>{l.msg}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button className="mk-btn-send" disabled={sending} onClick={handleSend}>
                {sending
                  ? <><span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"mkSpin .7s linear infinite", display:"inline-block" }}/> Sending…</>
                  : <><svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M13.5 7.5L2 2l2 5.5-2 5.5 11.5-5.5z" fill="white"/></svg>Send to {audienceCount.toLocaleString("en-IN")} customers</>
                }
              </button>
              <button className="mk-btn-ghost" style={{ width:"100%", marginTop:10 }} onClick={()=>setComposeStep(1)}>← Back</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // VIEW: DONE
  // ════════════════════════════════════════════════════════════════════════
  if(view===VIEWS.DONE) return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ ...CARD, textAlign:"center", padding:"44px 28px" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:"rgba(34,197,94,.13)", border:"2px solid rgba(34,197,94,.3)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M5 14l6.5 6.5L23 7" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <div style={{ fontSize:22, fontWeight:800, color:"var(--wd-t1)", marginBottom:8 }}>Campaign Sent!</div>
        <div style={{ fontSize:14, color:"var(--wd-t3)", marginBottom:26 }}>
          {audienceCount.toLocaleString("en-IN")} messages dispatched via <span style={{ fontFamily:"var(--wd-mono)", color:"#21A6E9" }}>{selectedTmpl?.name}</span>
        </div>
        <div style={{ background:"#090c13", border:"1px solid rgba(255,255,255,.06)", borderRadius:10, padding:"14px 18px", textAlign:"left", maxHeight:200, overflowY:"auto", marginBottom:26, fontFamily:"var(--wd-mono)" }}>
          {logs.map((l,i)=>(
            <div key={i} style={{ display:"flex", gap:10, marginBottom:4, fontSize:11 }}>
              <span style={{ color:"rgba(255,255,255,.2)", flexShrink:0 }}>{l.ts}</span>
              <span style={{ color:l.ok?"#22c55e":"rgba(255,255,255,.5)" }}>{l.msg}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
          <button className="mk-btn-primary"
            onClick={()=>{ setView(VIEWS.COMPOSE); setComposeStep(0); setSelectedTmpl(null); setSegments({all:true}); setCampaignName(""); setLogs([]); setProgress(0); }}>
            + New Campaign
          </button>
          <button className="mk-btn-ghost" onClick={()=>setView(VIEWS.LIST)}>Back to Templates</button>
        </div>
      </div>
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );

  return null;
}

// ─── Template card component ─────────────────────────────────────────────────
function TemplateCard({ t, onPreview, onUse }) {
  return (
    <div className="mk-tmpl-card"
      style={{ border:"1.5px solid rgba(255,255,255,.07)", borderRadius:12, padding:"16px 18px", cursor:"pointer", background:"rgba(255,255,255,.025)" }}
      onClick={()=>onPreview(t)}>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
        <div style={{ flex:1, minWidth:0, marginRight:8 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:4, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.display}</div>
          <div style={{ fontFamily:"var(--wd-mono)", fontSize:10, color:"var(--wd-t3)", marginBottom:6 }}>{t.name}</div>
        </div>
        <StatusBadge status={t.status}/>
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,.38)", lineHeight:1.55, display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden", marginBottom:12 }}>
        {t.body}
      </div>
      {t.sentCount>0 && (
        <div style={{ display:"flex", gap:12, marginBottom:10 }}>
          <span style={{ fontSize:11, color:"var(--wd-t3)" }}>Sent: <b style={{ color:"#21A6E9" }}>{t.sentCount.toLocaleString("en-IN")}</b></span>
          <span style={{ fontSize:11, color:"var(--wd-t3)" }}>Open: <b style={{ color:"#22c55e" }}>{t.openRate}%</b></span>
        </div>
      )}
      {t.variables.length>0 && (
        <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:10 }}>
          {t.variables.map(v=>(
            <code key={v} style={{ fontSize:10, background:"rgba(234,179,8,.1)", border:"1px solid rgba(234,179,8,.2)", padding:"1px 6px", borderRadius:4, fontFamily:"var(--wd-mono)", color:"#eab308" }}>{`{{${v}}}`}</code>
          ))}
        </div>
      )}
      <div style={{ paddingTop:10, borderTop:"1px solid rgba(255,255,255,.06)", display:"flex", gap:8 }}>
        <button className="mk-btn-ghost-sm" onClick={e=>{ e.stopPropagation(); onPreview(t); }}>Preview</button>
        {t.status==="APPROVED" && (
          <button className="mk-btn-ghost-sm" style={{ color:"#21A6E9", borderColor:"rgba(33,166,233,.3)" }}
            onClick={e=>{ e.stopPropagation(); onUse(); }}>
            Use →
          </button>
        )}
      </div>
    </div>
  );
}